/**
 * Color utility functions for calculating contrast and accessibility
 * Uses WCAG 2.1 contrast ratio guidelines for optimal text readability
 */

import { hex } from 'wcag-contrast'

/**
 * Returns the best contrasting text color (white or dark) for a given background color
 * Uses WCAG contrast ratio calculation to determine optimal text color
 * Picks whichever color (white or black) provides better contrast
 *
 * @param backgroundColor - Hex color string (e.g., '#FF5733' or '#F53')
 * @param lightColor - Color to use on dark backgrounds (default: 'white')
 * @param darkColor - Color to use on light backgrounds (default: '#1a1a1a')
 * @returns The contrasting color string
 */
export function getContrastingTextColor(
  backgroundColor: string,
  lightColor: string = 'white',
  darkColor: string = '#1a1a1a'
): string {
  try {
    // Calculate contrast ratios for both options
    const whiteContrast = hex('#FFFFFF', backgroundColor)
    const blackContrast = hex('#000000', backgroundColor)

    // Pick the color with better contrast ratio
    // WCAG AA requires 4.5:1 for normal text, 3:1 for large text
    return whiteContrast > blackContrast ? lightColor : darkColor
  } catch {
    // Fallback to dark text if color parsing fails
    return darkColor
  }
}
